import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Download, FileJson, FileText, Table } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { catalogEntries } from './catalogEntries'

function save(name: string, content: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

const csvCell = (s: string): string => `"${s.replace(/"/g, '""')}"`
const poStr = (s: string): string => `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`

/**
 * A22.11 — Import/eksport. Klient i18n katalogini yuklab olinadigan formatlarга
 * chiqaradi: JSON (key→{en,ru,uz}), CSV, va PO (gettext, til bo'yicha: msgid=en,
 * msgstr=tarjima). fn-qiymatlar (parametrli) chiqarib tashlanadi. Backend YO'Q —
 * hammasi brauzerда generatsiya qilinadi. (Import = yozuv-endpoint kutadi → DEPS.)
 */
export function LocalizationExport() {
  const t = useT()
  const rows = useMemo(() => catalogEntries().filter((e) => !e.fn), [])

  const asJson = () => {
    const obj: Record<string, { en: string; ru: string; uz: string }> = {}
    for (const e of rows) obj[e.key] = { en: e.en, ru: e.ru, uz: e.uz }
    save('grom-i18n.json', JSON.stringify(obj, null, 2), 'application/json')
  }

  const asCsv = () => {
    const lines = ['key,en,ru,uz', ...rows.map((e) => [e.key, e.en, e.ru, e.uz].map(csvCell).join(','))]
    save('grom-i18n.csv', lines.join('\n'), 'text/csv')
  }

  const asPo = (lang: 'ru' | 'uz') => {
    const head = `msgid ""\nmsgstr ""\n"Language: ${lang}\\n"\n"Content-Type: text/plain; charset=UTF-8\\n"\n`
    const body = rows
      .map((e) => `#: ${e.key}\nmsgid ${poStr(e.en)}\nmsgstr ${poStr(e[lang])}`)
      .join('\n\n')
    save(`grom-${lang}.po`, `${head}\n${body}\n`, 'text/x-gettext-translation')
  }

  const btn = 'inline-flex items-center gap-2 rounded-md border border-border-default bg-surface-2 px-3 py-2 text-sm text-primary hover:bg-surface-3'

  return (
    <>
      <PageHeader
        title={t.loc.exportTitle}
        screenId="A22.11"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />
      <div className="space-y-4 p-4">
        <p className="text-sm text-secondary">{t.loc.exportNote}</p>
        <p className="text-xs text-tertiary">{t.loc.exportCount(rows.length)}</p>
        <div className="flex flex-wrap gap-2">
          <button className={btn} onClick={asJson}><FileJson size={15} /> JSON</button>
          <button className={btn} onClick={asCsv}><Table size={15} /> CSV</button>
          <button className={btn} onClick={() => asPo('ru')}><FileText size={15} /> PO · RU</button>
          <button className={btn} onClick={() => asPo('uz')}><FileText size={15} /> PO · UZ</button>
        </div>
        <div className="flex items-start gap-2 rounded-md border border-border-subtle bg-surface-2 p-3 text-xs text-tertiary">
          <Download size={14} className="mt-0.5 shrink-0" />
          <span>{t.loc.importNote}</span>
        </div>
      </div>
    </>
  )
}
